'use client'

import React, { RefObject, useEffect, useMemo, useState } from 'react'
import {
  getActiveDeliveryZones,
  yandexService,
} from '@/_service/yandex.service'
import { countryGeometries } from '@/_constants'
import Marker from './components/Marker'
import GeolocationIcon from './components/GeolocationIcon'
import { Flex } from '@chakra-ui/react'

import './styles.css'
import { useTranslations } from 'next-intl'
import { getUserLocation } from '@/_utils/location'
import { IRedux } from '@/_store/types'
import { useSelector } from 'react-redux'
import { IDeliveryZone } from '@/_types'
import { useActiveBrand } from '@/_hooks/useActiveBrand'
import YandexMapFrame from '@/_components/Map/shared/mapBridge/YandexMapFrame'
import { useMapBridge, buildMapSrc } from '@/_components/Map/shared/mapBridge/useMapBridge'
import {
  DELIVERY_MARKER_ID,
  MAP_MODES,
  MESSAGE_TYPES,
} from '@/_components/Map/shared/mapBridge/constants'

interface IProps {
  mapRef: RefObject<any>
  errorZone: boolean
  isMobile?: boolean
  geometry: number[]
  outerLoading: boolean
  onMapBounded: (e: number[]) => void
  deliveryZone: IDeliveryZone | { id: string; min_order_amount: number } | null
  onLocationChange: (e: any, isSelect: boolean, findLocation?: boolean) => void
}

const DEFAULT_ZOOM = 17

const YandexMap: React.FC<IProps> = ({
  errorZone = false,
  isMobile = true,
  outerLoading = false,
  mapRef = null,
  geometry = countryGeometries['UZ'],
  deliveryZone = null,
  onLocationChange = () => {},
  onMapBounded = () => {},
}) => {
  const [innerLoading, setInnerLoading] = useState(false)
  const [isLocation, setIsLocation] = useState(false)
  const [determiningLocation, setDeterminingLocation] = useState(false)
  const [isReady, setIsReady] = useState(false)
  const [deliveryZones, setDeliveryZones] = useState<
    { id: string; points: number[][]; rgb_code?: string }[]
  >([])

  const t = useTranslations()
  const shipperId = useActiveBrand()

  const { isMapModal, isMapModalLg } = useSelector(
    (state: IRedux) => state.common
  )

  const getAddress = async (coords: number[]) => {
    setInnerLoading(true)
    try {
      const response = await yandexService.getGeocode({
        geocode: coords.join(','),
        lang: 'ru_RU',
      })

      const geoObject =
        response?.data?.response?.response?.GeoObjectCollection
          ?.featureMember?.[0]?.GeoObject

      if (geoObject) {
        const components =
          geoObject.metaDataProperty?.GeocoderMetaData?.Address?.Components ||
          []
        const values: Record<string, string> = {}
        let city = ''
        let house = ''

        components.forEach(({ kind, name }: { kind: string; name: string }) => {
          if (kind === 'house') {
            house = name
          }
          if (['locality', 'province'].includes(kind)) {
            city = name
          } else if (
            [
              'district',
              'street',
              'area',
              'house',
              'other',
              'vegetation',
            ].includes(kind)
          ) {
            values[kind] = name
          }
        })

        const formattedAddress = Object.values(values).join(', ') || city || ''

        onLocationChange(
          {
            address: formattedAddress,
            city,
            house,
            location: coords,
          },
          false
        )
      }
    } catch (error) {
      console.error('Failed to fetch address:', error)
    } finally {
      setInnerLoading(false)
    }
  }

  const { iframeRef, postToFrame } = useMapBridge(({ type, data }) => {
    switch (type) {
      case MESSAGE_TYPES.MAP_READY:
        setIsReady(true)
        break
      case MESSAGE_TYPES.MAP_CLICK: {
        const coords = data?.coords
        if (Array.isArray(coords) && coords.length === 2) {
          onMapBounded(coords)
          postToFrame(MESSAGE_TYPES.SET_CENTER, { center: coords, zoom: DEFAULT_ZOOM })
          postToFrame(MESSAGE_TYPES.ADD_MARKER, {
            id: DELIVERY_MARKER_ID,
            coords,
          })
          getAddress(coords)
        }
        break
      }
      case MESSAGE_TYPES.BOUNDS_CHANGED: {
        // Fired once the widget settles after a drag/pan — the map is
        // already centered there and the widget already moved its own
        // marker, so unlike MAP_CLICK there's nothing to echo back.
        const coords = data?.coords
        if (Array.isArray(coords) && coords.length === 2) {
          onMapBounded(coords)
          getAddress(coords)
        }
        break
      }
      default:
        break
    }
  })

  // Delivery-zone polygons, drawn by the widget from the coords we send it.
  useEffect(() => {
    const getActiveZones = async () => {
      try {
        const res = await getActiveDeliveryZones({}, shipperId)
        const result = res?.data?.delivery_zones?.map((item: any) => ({
          id: item.id,
          rgb_code: item.rgb_code,
          points: item?.points?.map((el: { lat: number; lon: number }) => [
            el.lat,
            el.lon,
          ]),
        }))
        setDeliveryZones(result || [])
      } catch (error) {
        console.error('Failed to fetch delivery zones:', error)
      }
    }

    getActiveZones()
  }, [shipperId])

  useEffect(() => {
    if (!isReady) return
    postToFrame(MESSAGE_TYPES.SET_ZONES, {
      zones: deliveryZones.map((zone) => ({
        id: zone.id,
        points: zone.points,
        fillColor: zone.rgb_code || '#ffff00',
        strokeColor: zone.rgb_code || '#ffff00',
      })),
    })
  }, [isReady, deliveryZones, postToFrame])

  // Exposes the same imperative `mapRef.current.setCenter(...)` contract
  // AddressSearch selection and geolocation already call.
  useEffect(() => {
    if (!mapRef) return
    mapRef.current = {
      setCenter: (coords: number[], zoom = DEFAULT_ZOOM) => {
        postToFrame(MESSAGE_TYPES.SET_CENTER, { center: coords, zoom })
        postToFrame(MESSAGE_TYPES.ADD_MARKER, {
          id: DELIVERY_MARKER_ID,
          coords,
        })
      },
    }
  }, [mapRef, postToFrame])

  const onGeolocate = () => {
    setDeterminingLocation(true)
    getUserLocation()
      .then((location) => {
        setIsLocation(false)
        if (location) {
          const coords = [location.latitude, location.longitude]
          postToFrame(MESSAGE_TYPES.SET_CENTER, { center: coords, zoom: DEFAULT_ZOOM })
          postToFrame(MESSAGE_TYPES.ADD_MARKER, {
            id: DELIVERY_MARKER_ID,
            coords,
          })
          onMapBounded(coords)
          getAddress(coords)
        }
      })
      .catch(() => {
        setIsLocation(true)
        setTimeout(() => {
          setIsLocation(false)
        }, 5000)
      })
      .finally(() => {
        setDeterminingLocation(false)
      })
  }

  useEffect(() => {
    if ((isMapModal || isMapModalLg) && isReady) {
      onGeolocate()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMapModal, isMapModalLg, isReady])

  const iframeSrc = useMemo(
    () =>
      buildMapSrc({
        lat: geometry[0],
        lng: geometry[1],
        zoom: DEFAULT_ZOOM,
        mode: MAP_MODES.DELIVERY,
      }),
    [geometry]
  )

  return (
    <Flex
      flex='1'
      zIndex={0}
      flexDir='column'
      overflow='hidden'
      position='relative'
      className='wrapper'
    >
      <Marker
        hover={false}
        errorZone={errorZone}
        zoneData={deliveryZone}
        isLocation={isLocation}
        isLoading={outerLoading || innerLoading}
      />
      <YandexMapFrame ref={iframeRef} src={iframeSrc} className='mmap' />
      <button
        type='button'
        className='geolocationControl'
        style={{
          position: 'absolute',
          bottom: isMobile ? '70px' : '20px',
          left: '50%',
        }}
        onClick={onGeolocate}
        disabled={determiningLocation}
      >
        <GeolocationIcon />
        {t('location_determination')}
      </button>
    </Flex>
  )
}

export default YandexMap
